import type { ElementRef } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    linkedSignal,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import type { ConnectedPosition } from '@angular/cdk/overlay';
import { OverlayModule } from '@angular/cdk/overlay';

import type { ThemeColor, ThemeColorName, ThemeColorsKeyValue } from '@sites/site-theme/src/types/theme-color.type';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpTransparentIconComponent } from '../../../../ui/sp-transparent-icon/src/sp-transparent-icon.component';
import { SpColorPickerDropdownComponent } from '../sp-color-picker-dropdown/sp-color-picker-dropdown.component';
import type { ColorPickerEventPayload } from '../types/color-picker.type';

/**
 * Color picker v2 — same picker content as v1, but the panel renders in a CDK Overlay
 * (a portal "outlet") instead of an inline ngx-bootstrap dropdown. The overlay panel is a
 * focus-trapped dialog: focus is captured on open, `Escape`/backdrop closes it, and focus
 * returns to the trigger on close.
 */
@Component({
    selector: 'sp-color-picker-v2',
    templateUrl: './sp-color-picker-v2.component.html',
    styleUrls: ['./sp-color-picker-v2.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [OverlayModule, A11yModule, SpTransparentIconComponent, SpColorPickerDropdownComponent],
})
export class SpColorPickerV2Component {
    public readonly color = input<string>('');
    public readonly ariaLabel = input('Color');
    public readonly colorsPreset = input<ThemeColor[]>([]);
    public readonly positionLeft = input(false);
    public readonly hideGradient = input(false);
    public readonly isDropup = input(false);

    public readonly colorChangeEvent = output<ColorPickerEventPayload>();

    private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');

    public readonly isOpen = signal(false);

    /** The preset palette as a name → literal lookup, for resolving `var(--…)` and theme names. */
    private readonly presetByName = computed<ThemeColorsKeyValue>(() =>
        this.colorsPreset().reduce((byName: ThemeColorsKeyValue, { name, value }) => {
            byName[name] = value;

            return byName;
        }, {})
    );

    /**
     * The swatch's literal colour.
     *
     * A `linkedSignal` because it has two sources: it follows the `color` input, and a pick inside
     * the panel sets it directly so the swatch updates before the parent echoes the change back —
     * which the parent may never do. That is exactly what the old `ngOnInit` + `ngOnChanges` pair
     * did, minus the `firstChange` guard that only existed to stop the two from fighting.
     */
    public readonly colorValue = linkedSignal(() => this.resolveColor(this.color()));

    public readonly overlayPositions = computed<ConnectedPosition[]>(() => {
        const isDropup = this.isDropup();
        const overlayY = isDropup ? 'bottom' : 'top';
        const originY = isDropup ? 'top' : 'bottom';
        const x = this.positionLeft() ? 'start' : 'end';
        const offsetY = isDropup ? -4 : 4;

        return [
            { originX: x, originY, overlayX: x, overlayY, offsetY },
            // flip vertically if there isn't room
            {
                originX: x,
                originY: isDropup ? 'bottom' : 'top',
                overlayX: x,
                overlayY: isDropup ? 'top' : 'bottom',
                offsetY: -offsetY,
            },
        ];
    });

    public toggle(): void {
        if (this.isOpen()) {
            this.close();

            return;
        }

        this.open();
    }

    public open(): void {
        this.isOpen.set(true);
    }

    public close(): void {
        if (!this.isOpen()) {
            return;
        }

        this.isOpen.set(false);
        // cdkTrapFocus restores focus on destroy, but return it explicitly to be safe.
        this.trigger()?.nativeElement.focus();
    }

    /**
     * The overlay panel lives in the CDK overlay container at the document root, so an
     * unhandled Escape bubbles to `document` and closes the host settings panel too. Stop
     * propagation so Escape only closes the picker.
     */
    public onEscape(event: Event): void {
        if (!this.isOpen()) {
            return;
        }

        event.stopPropagation();
        this.close();
    }

    public setColorHandler(value: string): void {
        this.emitPick(value);
    }

    public setColorPresetHandler({ value, name }: ThemeColor): void {
        this.emitPick(value, name);
    }

    private emitPick(value: string, presetName?: ThemeColorName): void {
        this.colorValue.set(this.resolveColor(value));
        this.colorChangeEvent.emit({ value: value.toLowerCase(), presetName });
    }

    private resolveColor(color: string): string {
        const presetByName = this.presetByName();

        if (color?.includes('var')) {
            const matches: RegExpMatchArray | null = color.match(/\((.*?)\)/);
            const matchedVariable = matches?.[1]?.slice(2) ?? '';
            const variableKey = Object.keys(presetByName).find((key) => matchedVariable.includes(key));

            return presetByName[variableKey as ThemeColorName]!;
        }

        return presetByName[color as ThemeColorName] || color;
    }
}
